/**
 * @fileoverview 内联工具函数（零框架依赖）
 *
 * 背景：本包曾依赖 `@peerup/helper` 的 `deepToRaw` / `liteFetch`，且 deepToRaw 内部
 * 硬 import vue 的 isReactive/isRef/toRaw/unref，导致 React 项目无法使用本包。
 *
 * 解法：core 不关心具体框架的响应式解包。cloneData 做通用的结构化克隆（递归展开
 * 普通对象/数组 + 循环引用保护 + Error 直传），框架特定的数据预处理（如 Vue 响应式
 * 解包）由接入方通过 {@link LoggerConfig.transformData} 钩子注入。
 *
 * @mark 本文件不引入任何外部运行时依赖，保证 logger 框架无关、自包含。
 */

/** 安全 JSON 序列化：循环引用等异常时返回空串（对齐 helper 实现） */
function safeJSONStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj)
  } catch (e) {
    console.error('[safeJSONStringify error]', e)
    return ''
  }
}

function sendJSONBeacon(url: string, data: unknown): boolean {
  if (!window.navigator.sendBeacon) return false
  try {
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json; charset=UTF-8' })
    return window.navigator.sendBeacon(url, blob)
  } catch {
    return false
  }
}

function sendUseXhrJSON(url: string, reqPayload: unknown): void {
  const xhr = new window.XMLHttpRequest()
  xhr.open('POST', `${url}?APIVersion=0.6.0`, true)
  xhr.setRequestHeader('content-type', 'application/json; charset=UTF-8')
  xhr.send(safeJSONStringify(reqPayload))
}

/**
 * 轻量化发起 json post 请求，默认走 sendBeacon（小 payload），降级 XHR
 * @description 行为对齐 @peerup/helper 的 liteFetch：payload ≥ 32KB 走 XHR，否则 sendBeacon
 */
export async function liteFetch(url: string, payload: string): Promise<void> {
  try {
    if (payload.length >= 32768) {
      sendUseXhrJSON(url, payload)
      return
    }
    if (!sendJSONBeacon(url, payload)) {
      sendUseXhrJSON(url, payload)
    }
  } catch (ex) {
    if (window?.console && typeof window.console.error === 'function') {
      console.error(`Failed to log to ali log service because of this exception:\n${ex}`)
      console.error('Failed log data:', url)
    }
  }
}

/**
 * 通用结构化克隆：递归展开普通对象/数组，保护循环引用，Error 直传。
 *
 * 框架特定的数据预处理（如 Vue 的 ref/reactive 解包）由接入方通过
 * {@link LoggerConfig.transformData} 注入，core 不硬编码框架逻辑。
 *
 * @param obj 待克隆的数据
 * @param cache 循环引用保护（WeakMap，内部递归用）
 */
export function cloneData<T = unknown>(
  obj: T,
  cache = new WeakMap<object, unknown>(),
): T {
  if (obj == null || typeof obj !== 'object') return obj

  // 循环引用保护：已在处理中的对象直接返回原值
  const cached = cache.get(obj as object)
  if (cached !== undefined) return cached as T
  cache.set(obj as object, obj)

  // 数组
  if (Array.isArray(obj)) {
    const result = obj.map(item => cloneData(item, cache)) as T
    cache.set(obj as object, result)
    return result
  }

  // Error 直传（不展开内部属性）
  if (obj instanceof Error) return obj

  // 普通对象：递归处理各属性
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(obj as object)) {
    result[key] = cloneData((obj as Record<string, unknown>)[key], cache)
  }
  return result as T
}
